import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Check } from 'lucide-react';
import styles from './ClaySelect.module.css';

export interface ClaySelectOption {
  value: string;
  label: string;
}

interface ClaySelectProps {
  value: string;
  options: ClaySelectOption[];
  onChange: (value: string) => void;
  label?: string;
}

export function ClaySelect({ value, options, onChange, label }: ClaySelectProps) {
  const [open, setOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedLabel = options.find(o => o.value === value)?.label ?? '';

  const calcPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const dropdownHeight = options.length * 40 + 8; // estimate
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const openUpward = spaceBelow < dropdownHeight && rect.top > dropdownHeight;

    setDropdownStyle({
      position: 'fixed',
      left: rect.left,
      width: rect.width,
      ...(openUpward
        ? { bottom: window.innerHeight - rect.top + 6 }
        : { top: rect.bottom + 6 }),
    });
  }, [options.length]);

  const openDropdown = () => {
    calcPosition();
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const reposition = () => calcPosition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, calcPosition]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || dropdownRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const select = (val: string) => {
    onChange(val);
    setOpen(false);
  };

  const dropdown = (
    <AnimatePresence>
      {open && (
        <motion.div
          ref={dropdownRef}
          className={styles.dropdown}
          style={dropdownStyle}
          initial={{ opacity: 0, y: -6, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -6, scale: 0.97 }}
          transition={{ type: 'spring', stiffness: 420, damping: 28 }}
        >
          <ul className={styles.list}>
            {options.map((opt) => {
              const isSelected = opt.value === value;
              return (
                <motion.li key={opt.value}>
                  <motion.button
                    type="button"
                    onClick={() => select(opt.value)}
                    className={[styles.option, isSelected ? styles['option-selected'] : ''].join(' ')}
                    whileTap={{ scale: 0.98 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                  >
                    <span className={styles['option-label']}>{opt.label}</span>
                    {isSelected && (
                      <motion.span
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring', stiffness: 600, damping: 20 }}
                      >
                        <Check style={{ width: 13, height: 13, flexShrink: 0 }} />
                      </motion.span>
                    )}
                  </motion.button>
                </motion.li>
              );
            })}
          </ul>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <div className={styles.root}>
      {label && <span className={styles.label}>{label}</span>}

      <motion.button
        ref={triggerRef}
        type="button"
        onClick={() => (open ? setOpen(false) : openDropdown())}
        className={[styles.trigger, open ? styles['trigger-open'] : ''].join(' ')}
        whileTap={{ scale: 0.98 }}
        transition={{ type: 'spring', stiffness: 500, damping: 25 }}
      >
        <span className={styles['trigger-value']}>{selectedLabel}</span>
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ type: 'spring', stiffness: 400, damping: 28 }}
          style={{ display: 'flex', flexShrink: 0 }}
        >
          <ChevronDown style={{ width: 14, height: 14 }} className={styles.chevron} />
        </motion.span>
      </motion.button>

      {createPortal(dropdown, document.body)}
    </div>
  );
}
